import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SUPPLIES, BUSINESSES } from "@/data/gameData";
import { useGame } from "@/context/GameContext";
import { Check, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Sound } from "@/lib/sound";

const Shop = ({ open, onClose, businessFilter }) => {
  const { state, buySupply } = useGame();
  const items = businessFilter
    ? SUPPLIES.filter((s) => s.business === businessFilter)
    : SUPPLIES;

  const handleBuy = (s) => {
    if (state.ownedSupplies.includes(s.id)) return;
    if (state.cash < s.price) {
      Sound.error();
      toast.error(`Not enough cash! You need $${s.price}.`);
      return;
    }
    Sound.purchase();
    buySupply(s);
    toast.success(`Bought ${s.name}! ${s.boost}`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-3xl border-4 border-amber-300 bg-white p-0 overflow-hidden max-h-[85vh] overflow-y-auto" data-testid="shop-modal">
        <div className="bg-gradient-to-br from-orange-300 to-amber-400 p-5 border-b-4 border-amber-500 sticky top-0">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-8 h-8 text-slate-900" />
              <div>
                <div className="text-[11px] uppercase tracking-widest font-black text-amber-900">
                  Supplies Shop · Fixed vs Variable Costs
                </div>
                <DialogTitle className="font-display text-2xl font-bold text-slate-900">
                  Upgrade your business
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-600 font-medium bg-blue-50 border-2 border-blue-200 rounded-2xl p-3">
            💡 <strong>Supplies are business expenses.</strong> One-time purchases boost your earnings on every job — that&apos;s a smart investment!
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {items.map((s) => {
              const owned = state.ownedSupplies.includes(s.id);
              const biz = BUSINESSES[s.business];
              return (
                <div
                  key={s.id}
                  className={`rounded-2xl p-4 border-2 ${biz.borderColor} ${biz.lightBg}`}
                  data-testid={`shop-item-${s.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-4xl">{s.emoji}</div>
                    <div className="flex-1">
                      <div className="font-heading font-black text-slate-900">{s.name}</div>
                      <div className="text-[10px] uppercase font-black text-slate-500 tracking-wider">
                        {biz.name}
                      </div>
                      <div className={`text-xs font-bold mt-1 ${biz.textColor}`}>{s.boost}</div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleBuy(s)}
                    disabled={owned}
                    data-testid={`buy-${s.id}`}
                    className={`w-full mt-3 font-black font-heading rounded-xl btn-3d py-4 ${
                      owned
                        ? "bg-emerald-500 hover:bg-emerald-500 text-white border-2 border-emerald-600"
                        : `${biz.solidBg} ${biz.hoverBg} text-white border-2 border-black/10`
                    }`}
                  >
                    {owned ? (
                      <>
                        <Check className="w-4 h-4 mr-1" /> Owned
                      </>
                    ) : (
                      `Buy — $${s.price}`
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Shop;
